import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Configurations are org-wide (not project-scoped), so this uses its own axios
// instance rather than the shared one that attaches X-Project-Id — mirrors roleApi.ts.
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

export type ConfigurationType = 'translation-text-correction';
export type TextCorrectionMatchType = 'word' | 'substring' | 'regex';

export interface TextCorrectionRule {
  find: string;
  replace: string;
  matchType: TextCorrectionMatchType;
  caseSensitive: boolean;
}

export interface ConfigurationData {
  rules?: TextCorrectionRule[];
  /** Convert standalone numbers in the translated text to words, in the configuration's language. */
  convertNumbersToWords?: boolean;
  /** Separate the letters of ALL-CAPS abbreviations with dots (e.g. "NASA" -> "N.A.S.A."). */
  separateAbbreviationsWithDots?: boolean;
}

export interface Configuration {
  _id: string;
  organizationId: string;
  type: ConfigurationType;
  name: string;
  description?: string;
  language?: string;
  isActive: boolean;
  data: ConfigurationData;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ConfigurationInput {
  type: ConfigurationType;
  name: string;
  description?: string;
  language?: string;
  isActive?: boolean;
  data: ConfigurationData;
}

export const configurationApi = {
  getAll: (params?: { type?: ConfigurationType; language?: string; isActive?: boolean }) =>
    api.get<{ success: boolean; data: { configurations: Configuration[] } }>('/configurations', { params }),

  getById: (id: string) =>
    api.get<{ success: boolean; data: { configuration: Configuration } }>(`/configurations/${id}`),

  create: (data: ConfigurationInput) =>
    api.post<{ success: boolean; message: string; data: { configuration: Configuration } }>('/configurations', data),

  update: (id: string, data: Partial<ConfigurationInput>) =>
    api.put<{ success: boolean; message: string; data: { configuration: Configuration } }>(`/configurations/${id}`, data),

  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/configurations/${id}`),
};

export default configurationApi;
